import { Grid, Stack, TypographyProps } from '@mui/material';
import { ReactNode } from 'react';
import { TypographyWithTooltip } from 'components/TypographyWithTooltip';
import { EMPTY_DATA_VALUE } from '../../constants';
import { StatHeaderLabel } from './StatHeaderLabel';

export type StatItem = {
  label: string;
  value: ReactNode;
  testId?: string;
  valueTypographyProps?: TypographyProps;
  tooltipText?: string;
};

type StatTableProps = {
  stats: StatItem[];
  itemSize?: 3 | 4 | 6 | 12;
};

const STAT_ITEM_SIZE_BY_COUNT: Record<number, 3 | 4 | 6 | 12> = {
  1: 12,
  2: 6,
  3: 4,
  4: 3,
};

/**
 * Returns the column span (out of 12) that spreads `count` stats evenly across
 * a single row: 1 -> 12, 2 -> 6, 3 -> 4, 4 -> 3. For 5 or more it returns the
 * smallest span (3), so the result is always one of the valid grid sizes.
 */
export const getStatItemSize = (count: number): 3 | 4 | 6 | 12 =>
  STAT_ITEM_SIZE_BY_COUNT[count] ?? 3;

export const StatTable = (props: StatTableProps): JSX.Element => {
  const { stats, itemSize = 4 } = props;
  const columnsPerRow = 12 / itemSize;

  return (
    <Grid container component="dl" role="list" sx={{ rowGap: 1 }}>
      {stats.map(
        (
          { label, value, testId, valueTypographyProps = {}, tooltipText },
          index,
        ) => {
          const displayValue = value ?? EMPTY_DATA_VALUE;
          const valueTitle =
            typeof displayValue === 'string' || typeof displayValue === 'number'
              ? String(displayValue)
              : '';

          return (
            <Grid
              key={label}
              size={itemSize}
              role="listitem"
              sx={{
                display: 'flex',
                textAlign: 'center',
                borderRight:
                  stats.length > 1 && (index + 1) % columnsPerRow !== 0 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Stack
                data-testid={testId}
                sx={{ margin: 1, gap: 0.5, width: '100%' }}
              >
                <StatHeaderLabel label={label} tooltipText={tooltipText} />
                <TypographyWithTooltip
                  component="dd"
                  variant="body1"
                  title={valueTitle}
                  noWrap={Boolean(valueTitle)}
                  fontWeight="fontWeightBold"
                  {...valueTypographyProps}
                  sx={{
                    minHeight: 24, // Match MUI Chip size="small" height
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    ...valueTypographyProps.sx,
                  }}
                >
                  {displayValue}
                </TypographyWithTooltip>
              </Stack>
            </Grid>
          );
        },
      )}
    </Grid>
  );
};
